import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  AlertTriangle,
  Flag,
  CheckCircle,
  Clock,
  User,
  MessageSquare,
  Eye,
  RefreshCw,
  FileText,
  Loader2
} from 'lucide-react';
const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-amber-100 text-amber-800',
  urgent: 'bg-red-100 text-red-800'
};

const NOTE_TYPE_COLORS = {
  general: 'bg-gray-100 text-gray-700',
  observation: 'bg-blue-100 text-blue-700',
  concern: 'bg-amber-100 text-amber-700',
  action_item: 'bg-green-100 text-green-700'
};

export default function AdminFlaggedSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchFlaggedSessions();
  }, []);

  const fetchFlaggedSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/sessions/flagged');
      setSessions(response.data.sessions || []);
    } catch (error) {
      toast.error('Failed to load flagged sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSession = (session) => {
    setSelectedSession(session);
    setShowDetailDialog(true);
  };

  const handleResolve = async (sessionId) => {
    setResolving(true);
    try {
      await axios.put(`/api/admin/sessions/${sessionId}/resolve`);
      toast.success('Session marked as resolved');
      setShowDetailDialog(false);
      fetchFlaggedSessions();
    } catch (error) {
      toast.error('Failed to resolve session');
    } finally {
      setResolving(false);
    }
  };

  const escalatedSessions = sessions.filter(s => s.needsAdminReview);
  const flaggedOnlySessions = sessions.filter(s => !s.needsAdminReview && s.flagCount > 0);

  return (
      <div data-testid="admin-flagged-sessions">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Sessions Needing Review
            </h1>
            <p className="text-muted-foreground">
              Escalated sessions and flagged questions requiring attention
            </p>
          </div>
          <Button variant="outline" onClick={fetchFlaggedSessions} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Flagged</p>
                  <p className="text-3xl font-bold">{sessions.length}</p>
                </div>
                <Flag className="w-8 h-8 text-amber-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">Escalated</p>
                  <p className="text-3xl font-bold text-red-700">{escalatedSessions.length}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Flagged Questions</p>
                  <p className="text-3xl font-bold">{flaggedOnlySessions.length}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-blue-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sessions Tabs */}
        <Tabs defaultValue="escalated" className="space-y-4">
          <TabsList>
            <TabsTrigger value="escalated" className="gap-2">
              <AlertTriangle className="w-4 h-4" />
              Escalated ({escalatedSessions.length})
            </TabsTrigger>
            <TabsTrigger value="flagged" className="gap-2">
              <Flag className="w-4 h-4" />
              Flagged Questions ({flaggedOnlySessions.length})
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              All ({sessions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="escalated">
            <SessionsTable 
              sessions={escalatedSessions} 
              loading={loading}
              onView={handleViewSession}
              showEscalation
            />
          </TabsContent>

          <TabsContent value="flagged">
            <SessionsTable 
              sessions={flaggedOnlySessions} 
              loading={loading}
              onView={handleViewSession}
            />
          </TabsContent>

          <TabsContent value="all">
            <SessionsTable 
              sessions={sessions} 
              loading={loading}
              onView={handleViewSession}
              showEscalation
            />
          </TabsContent>
        </Tabs>

        {/* Session Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedSession && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {selectedSession.participant?.firstName} {selectedSession.participant?.lastName}
                  </DialogTitle>
                  <DialogDescription>
                    {selectedSession.participant?.email} • {selectedSession.type === 'fear' ? 'EDGE Diagnostic' : 'CORE Diagnostic'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Escalation Info */}
                  {selectedSession.escalation && (
                    <Card className="border-amber-200 bg-amber-50/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          Escalation Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge className={PRIORITY_COLORS[selectedSession.escalation.priority]}>
                            {selectedSession.escalation.priority?.toUpperCase()}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            Escalated by {selectedSession.escalation.escalatedByName}
                          </span>
                        </div>
                        <p className="text-sm">{selectedSession.escalation.reason}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(selectedSession.escalation.escalatedAt)}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Session Notes */}
                  {selectedSession.sessionNotes && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Session Notes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{selectedSession.sessionNotes}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Timestamped Notes */}
                  {selectedSession.timestampedNotes?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Timestamped Notes ({selectedSession.timestampedNotes.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {selectedSession.timestampedNotes.map((note, idx) => (
                            <div key={idx} className="border-l-2 border-primary/30 pl-3">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={NOTE_TYPE_COLORS[note.noteType] || NOTE_TYPE_COLORS.general} variant="secondary">
                                  {note.noteType?.replace('_', ' ')}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {note.createdByName} • {formatDateTime(note.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm">{note.content}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Flagged Questions */}
                  {selectedSession.flaggedQuestions?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Flag className="w-4 h-4 text-red-500" />
                          Flagged Questions ({selectedSession.flaggedQuestions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedSession.flaggedQuestions.map((flag, idx) => (
                            <div key={idx} className="p-2 bg-red-50 rounded border border-red-100">
                              <p className="text-sm font-medium">Question {flag.questionIndex + 1}</p>
                              {flag.reason && (
                                <p className="text-sm text-muted-foreground">{flag.reason}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`/admin/participants/${selectedSession.participant?.id}`, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Participant
                  </Button>
                  {selectedSession.needsAdminReview && (
                    <Button 
                      onClick={() => handleResolve(selectedSession.id)}
                      disabled={resolving}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {resolving ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-1" />
                      )}
                      Mark Resolved
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
}

// Sessions Table Component
function SessionsTable({ sessions, loading, onView, showEscalation = false }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
          <p className="text-muted-foreground">No sessions requiring review</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Participant</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Date</TableHead>
            {showEscalation && <TableHead>Priority</TableHead>}
            <TableHead>Flags</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell>
                <div>
                  <p className="font-medium">
                    {session.participant?.firstName} {session.participant?.lastName}
                  </p>
                  <p className="text-sm text-muted-foreground">{session.participant?.email}</p>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">
                  {session.type === 'fear' ? 'EDGE' : 'CORE'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(session.createdAt)}
              </TableCell>
              {showEscalation && (
                <TableCell>
                  {session.escalation?.priority && (
                    <Badge className={PRIORITY_COLORS[session.escalation.priority]}>
                      {session.escalation.priority}
                    </Badge>
                  )}
                </TableCell>
              )}
              <TableCell>
                <div className="flex items-center gap-2">
                  {session.needsAdminReview && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Escalated
                    </Badge>
                  )}
                  {session.flagCount > 0 && (
                    <Badge variant="outline" className="gap-1">
                      <Flag className="w-3 h-3" />
                      {session.flagCount}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline" onClick={() => onView(session)}>
                  <Eye className="w-4 h-4 mr-1" />
                  Review
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
